/**
 * Image Extraction Utilities for S3 Resource Migration
 *
 * This module provides functions to extract base64 images from HTML,
 * replace them with placeholders, and restore images from resources.
 */

const DATA_IMAGE_SRC_PATTERN = /^data:image\/([^;]+);base64,(.+)$/;

/**
 * Parses a data-URI image src into format and base64 payload.
 * @param {string|null} src - img src attribute
 * @returns {{format: string, base64: string}|null}
 */
export const parseDataImageSrc = (src) => {
  if (!src || typeof src !== 'string') {
    return null;
  }

  const match = src.match(DATA_IMAGE_SRC_PATTERN);
  if (!match) {
    return null;
  }

  return { format: match[1], base64: match[2] };
};

/**
 * Walks img nodes in HTML and optionally replaces them via the DOM.
 * Returning a string from `replacer` sets that node's outerHTML; returning
 * null/undefined leaves the node unchanged.
 *
 * @param {string} html - HTML string
 * @param {(img: Element, index: number) => string|null|undefined} replacer
 * @returns {string} Serialized body HTML
 */
export const replaceImgNodes = (html, replacer) => {
  if (!html || typeof html !== 'string') {
    return html;
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const imgElements = Array.from(doc.querySelectorAll('img'));

  imgElements.forEach((img, index) => {
    const replacement = replacer(img, index);
    if (typeof replacement === 'string') {
      img.outerHTML = replacement;
    }
  });

  return doc.body.innerHTML;
};

/**
 * Extracts all base64-encoded images from HTML content
 * @param {string} html - HTML string potentially containing base64 images
 * @returns {Array<{base64: string, format: string, originalTag: string, fullSrc: string}>}
 */
export const extractImagesFromHtml = (html) => {
  if (!html || typeof html !== 'string') {
    return [];
  }

  const images = [];
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const imgElements = doc.querySelectorAll('img');

  imgElements.forEach((img) => {
    const parsed = parseDataImageSrc(img.getAttribute('src'));
    if (parsed) {
      images.push({
        base64: parsed.base64,
        format: parsed.format,
        originalTag: img.outerHTML,
        fullSrc: img.getAttribute('src'),
        alt: img.getAttribute('alt') || '',
        title: img.getAttribute('title') || ''
      });
    }
  });

  return images;
};

/**
 * Extracts resource ID from img tag if it has data-resource-id attribute
 * @param {string} imgTag - HTML img tag string
 * @returns {number|null} Resource ID or null if not found
 */
export const getResourceIdFromImgTag = (imgTag) => {
  const match = imgTag.match(/data-resource-id=["'](\d+)["']/);
  return match ? parseInt(match[1], 10) : null;
};

/**
 * Extracts all images (both S3 resources and base64) from HTML
 * Returns separate arrays for existing resources and new base64 images
 * @param {string} html - HTML string
 * @returns {{resourceImages: Array, base64Images: Array}}
 */
export const extractAllImagesFromHtml = (html) => {
  if (!html || typeof html !== 'string') {
    return { resourceImages: [], base64Images: [] };
  }

  const resourceImages = [];
  const base64Images = [];
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const imgElements = doc.querySelectorAll('img');

  imgElements.forEach((img) => {
    const src = img.getAttribute('src');
    const resourceId = img.getAttribute('data-resource-id');

    if (resourceId) {
      // This is an existing S3 resource
      resourceImages.push({
        id: parseInt(resourceId, 10),
        originalTag: img.outerHTML
      });
    } else {
      const parsed = parseDataImageSrc(src);
      if (parsed) {
        base64Images.push({
          base64: parsed.base64,
          format: parsed.format,
          originalTag: img.outerHTML,
          fullSrc: src,
          alt: img.getAttribute('alt') || '',
          title: img.getAttribute('title') || ''
        });
      }
    }
  });

  return { resourceImages, base64Images };
};

/**
 * Replaces all img tags (both base64 and resource images) with {{ img_tag(N) }} placeholders
 * Maintains global position numbering
 * @param {string} html - HTML string with img tags
 * @returns {string} HTML with placeholders
 */
export const replaceImagesWithPlaceholders = (html) => {
  return replaceImgNodes(html, (_img, index) => `{{ img_tag(${index}) }}`);
};

/**
 * Replaces {{ img_tag(N) }} placeholders with actual img tags using resources array
 * @param {string} html - HTML string with placeholders
 * @param {Array} resources - Array of resource objects with signed_url, id, name, caption
 * @param {boolean} addResourceIdAttribute - Whether to add data-resource-id attribute (for editor)
 * @returns {string} HTML with img tags
 */
export const replacePlaceholdersWithImages = (html, resources = [], addResourceIdAttribute = false) => {
  if (!html || typeof html !== 'string') {
    return html;
  }

  if (!resources || resources.length === 0) {
    return html;
  }

  let result = html;

  // Sort resources by position to ensure correct replacement
  const sortedResources = [...resources].sort((a, b) => a.position - b.position);

  // Replace each placeholder with corresponding image
  sortedResources.forEach((resource) => {
    const placeholder = `{{ img_tag(${resource.position}) }}`;
    const resourceIdAttr = addResourceIdAttribute ? ` data-resource-id="${resource.id}"` : '';
    const altText = resource.caption || resource.name || '';
    const imgTag = `<img src="${resource.signed_url}"${resourceIdAttr} alt="${altText}" title="${altText}" />`;

    result = result.replace(placeholder, imgTag);
  });

  return result;
};

/**
 * Detects image format from base64 string
 * @param {string} base64String - Base64 encoded image data
 * @returns {string} Image format (png, jpg, gif, etc.) or 'unknown'
 */
export const getImageFormat = (base64String) => {
  if (!base64String) {
    return 'unknown';
  }

  // Check magic numbers (first few bytes) of common image formats
  const signatures = {
    '/9j/': 'jpg',
    'iVBORw0KGgo': 'png',
    'R0lGODlh': 'gif',
    'R0lGODdh': 'gif',
    'UklGR': 'webp',
  };

  for (const [signature, format] of Object.entries(signatures)) {
    if (base64String.startsWith(signature)) {
      return format;
    }
  }

  return 'unknown';
};

/**
 * Generates a filename for an image based on format and timestamp
 * @param {string} format - Image format (png, jpg, etc.)
 * @param {number} index - Image index in the sequence
 * @returns {string} Generated filename
 */
export const generateImageFilename = (format, index) => {
  const timestamp = Date.now();
  const cleanFormat = format === 'jpg' ? 'jpg' : format;
  return `image_${index}_${timestamp}.${cleanFormat}`;
};

/**
 * Counts the number of img_tag placeholders in HTML
 * @param {string} html - HTML string
 * @returns {number} Number of placeholders found
 */
export const countImagePlaceholders = (html) => {
  if (!html || typeof html !== 'string') {
    return 0;
  }

  const matches = html.match(/{{ img_tag\(\d+\) }}/g);
  return matches ? matches.length : 0;
};

/**
 * Validates that placeholder positions are sequential (0, 1, 2, ...)
 * @param {string} html - HTML string with placeholders
 * @returns {boolean} True if positions are valid and sequential
 */
export const validatePlaceholderPositions = (html) => {
  if (!html || typeof html !== 'string') {
    return true;
  }

  const matches = html.match(/{{ img_tag\((\d+)\) }}/g);
  if (!matches) {
    return true;
  }

  const positions = matches.map(match => {
    const posMatch = match.match(/\((\d+)\)/);
    return posMatch ? parseInt(posMatch[1], 10) : -1;
  });

  // Check if positions are sequential starting from 0
  for (let i = 0; i < positions.length; i++) {
    if (positions[i] !== i) {
      return false;
    }
  }

  return true;
};
